import React, { useState, useEffect } from 'react';
import { fetchNozzles, saveNozzleReading, fetchNozzleReadings } from '../../services/api';
import './MeterReading.css';

const today = '04-12-2025';

export default function MeterReading({ user }) {
  const companyId = user?.Companyid || user?.company_id || "1";
  const [readingDate, setReadingDate] = useState(formatDateForUI(new Date()));
  const [shiftNo, setShiftNo] = useState('1');
  const [location, setLocation] = useState('Pump');
  const [cashAC, setCashAC] = useState('Cash In Hand 2');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadData();
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Nozzles and ALL Nozzle Readings
      const [nozelsRes, readsRes] = await Promise.all([
        fetchNozzles(companyId),
        fetchNozzleReadings({ Companyid: companyId })
      ]);

      if (nozelsRes.success && nozelsRes.Response) {
        const nozzlesArr = Array.isArray(nozelsRes.Response) ? nozelsRes.Response : [nozelsRes.Response];
        const readingsArr = Array.isArray(readsRes.Response) ? readsRes.Response : [];

        const apiDate = formatDateForAPI(readingDate);
        const apiShift = shiftNo === '1' ? 'A' : (shiftNo === '2' ? 'B' : 'C');

        const combined = nozzlesArr.map(nz => {

          // ✅ Get latest record for this nozzle
          const latest = readingsArr
            .filter(r => String(r.Nozzleid) === String(nz.Nozzleid))
            .sort((a, b) => (b.ClosingNRid || 0) - (a.ClosingNRid || 0))[0];

          const openVal = latest ? parseFloat(latest.OpeningReading || 0) : 0;
          const closeVal = latest ? parseFloat(latest.ClosingReading || openVal) : openVal;

          return {
            id: nz.Nozzleid,
            unitName: `${nz.NozzleName} (${nz.PumpName})`,
            product: nz.ProductName,

            // ✅ IMPORTANT (used for update)
            openId: latest ? latest.OpeningNRid : 0,
            closeId: latest ? latest.ClosingNRid : 0,

            openRead: openVal,
            closeRead: closeVal,

            saleQty: Math.max(0, closeVal - openVal),
            itemRate: parseFloat(nz.Rate || 108.34),

            testing: 0,
            evapLoss: 0,
            taxPer: 0,
            tax: 0,
            amount: Math.max(0, closeVal - openVal) * (nz.Rate || 108.34)
          };
        });
        setItems(combined);
      }
    } catch (err) {
      console.error("MeterReading load error:", err);
    } finally {
      setLoading(false);
    }
  };

  function formatDateForUI(d) {
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    let year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  }

  function formatDateForAPI(dateStr) {
    if (!dateStr || !dateStr.includes('-')) return dateStr;
    const [y, m, d] = dateStr.split('-');
    return `${d}-${m}-${y}`;
  }

  const handleItemChange = (id, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: parseFloat(value) || 0 };
      if (field === 'closeRead' || field === 'openRead') {
        updated.saleQty = Math.max(0, updated.closeRead - updated.openRead);
        updated.amount = updated.saleQty * updated.itemRate;
      }
      if (field === 'saleQty' || field === 'itemRate') {
        updated.amount = updated.saleQty * updated.itemRate;
      }
      return updated;
    }));
  };

  const computedTotals = (() => {
    const result = { CNG: { q: 0, a: 0 }, Diesel: { q: 0, a: 0 }, Petrol: { q: 0, a: 0 } };
    items.forEach(item => {
      const n = item.unitName.toLowerCase();
      const p = (item.product || '').toLowerCase();
      const k = p.includes('cng') || n.includes('cng') ? 'CNG' :
        (p.includes('diesel') || n.includes('diesel') || p.includes('hsd') || n.includes('hsd')) ? 'Diesel' : 'Petrol';
      result[k].q += item.saleQty;
      result[k].a += item.amount;
    });
    return result;
  })();

  const grandTotal = items.reduce((s, i) => s + i.amount, 0);

  const handleSave = async () => {
    setLoading(true);
    try {
      const apiDate = formatDateForAPI(readingDate);
      const apiShift = shiftNo === '1' ? 'A' : (shiftNo === '2' ? 'B' : 'C');

      for (const item of items) {
        // Only save if values were entered or changed
        if (item.closeRead >= item.openRead) {
          const payloadBase = {
            Companyid: companyId,
            NozzleName: item.unitName.split(' (')[0],
            Product: item.product,
            Status: "Active",
            Date1: apiDate,
            Shift: apiShift,
          };

          // 1. Save Opening Reading
          await saveNozzleReading({
            ...payloadBase,
            Reading: item.openRead.toString(),
            Type: "Opening",
            IDS: item.openId ? String(item.openId) : "0",
            NRid: item.openId ? String(item.openId) : "0"
          });

          // 2. Save Closing Reading
          await saveNozzleReading({
            ...payloadBase,
            Reading: item.closeRead.toString(),
            Type: "Closing",
            IDS: item.closeId ? String(item.closeId) : "0",
            NRid: item.closeId ? String(item.closeId) : "0"
          });
        }
      }
      setSaved(true);
      await loadData();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save meter readings:", err);
      alert("Error saving readings. Please check your network or console.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="meter-reading-page">
      <div className="page-header">
        <h2 className="page-title">Meter Reading Entry</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-save me-1"></i> Save Reading
              </>
            )}
          </button>

          {saved && (
            <span style={{ color: 'var(--secondary)', fontWeight: 600, padding: '6px 0' }}>
              <i className="bi bi-check-circle me-1"></i> Saved!
            </span>
          )}
        </div>
      </div>

      <div className="section-card" style={{ marginBottom: 14 }}>
        <div className="form-row" style={{ padding: '12px 16px' }}>
          <div className="form-group"><label>Date</label><input className="form-control" value={readingDate} onChange={e => setReadingDate(e.target.value)} /></div>
          <div className="form-group"><label>Shift No.</label><select className="form-control" value={shiftNo} onChange={e => setShiftNo(e.target.value)}><option>1</option><option>2</option><option>3</option></select></div>
          <div className="form-group"><label>Location</label><select className="form-control" value={location} onChange={e => setLocation(e.target.value)}><option>Pump</option><option>Tank</option></select></div>
          <div className="form-group"><label>Cash A/C</label><input className="form-control" value={cashAC} onChange={e => setCashAC(e.target.value)} /></div>
        </div>
      </div>

      <div className="section-card">
        <div className="section-header">Nozzle Readings</div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-left">Unit</th>
                <th className="text-center">Open Reading</th>
                <th className="text-center">Close Reading</th>
                <th className="text-right">Sale Qty</th>
                <th className="text-right">Rate (₹)</th>
                <th className="text-right">Testing</th>
                <th className="text-right">Evap Loss</th>
                <th className="text-right">Tax %</th>
                <th className="text-right">Tax</th>
                <th className="text-right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  <td className="text-left" style={{ fontWeight: 600, minWidth: 220 }}>{item.unitName}</td>
                  <td className="text-center"><input type="number" style={{ width: 110 }} className="form-control text-center" value={item.openRead} onChange={e => handleItemChange(item.id, 'openRead', e.target.value)} /></td>
                  <td className="text-center"><input type="number" style={{ width: 110 }} className="form-control text-center" value={item.closeRead} onChange={e => handleItemChange(item.id, 'closeRead', e.target.value)} /></td>
                  <td className="text-right" style={{ fontWeight: 700, color: item.saleQty > 0 ? 'var(--primary)' : 'inherit' }}>{item.saleQty.toFixed(3)}</td>
                  <td className="text-right"><input type="number" style={{ width: 90 }} className="form-control text-right" value={item.itemRate} onChange={e => handleItemChange(item.id, 'itemRate', e.target.value)} /></td>
                  <td className="text-right"><input type="number" style={{ width: 80 }} className="form-control text-right" value={item.testing} onChange={e => handleItemChange(item.id, 'testing', e.target.value)} /></td>
                  <td className="text-right"><input type="number" style={{ width: 80 }} className="form-control text-right" value={item.evapLoss} onChange={e => handleItemChange(item.id, 'evapLoss', e.target.value)} /></td>
                  <td className="text-right"><input type="number" style={{ width: 60 }} className="form-control text-right" value={item.taxPer} onChange={e => handleItemChange(item.id, 'taxPer', e.target.value)} /></td>
                  <td className="text-right" style={{ fontFamily: 'monospace' }}>{item.tax.toFixed(2)}</td>
                  <td className="text-right" style={{ fontWeight: 700 }}>{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="section-card" style={{ marginTop: 14 }}>
        <div className="section-header">Summary</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 12, padding: 16 }}>
          {Object.entries(computedTotals).map(([k, v]) => (
            <div key={k} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid var(--primary)' }}>
              <div style={{ fontWeight: 800, color: 'var(--primary)', marginBottom: 4 }}>{k}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Qty: <b style={{ color: 'var(--text-main)' }}>{v.q.toFixed(3)} L</b></div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Amount: <b style={{ color: 'var(--text-main)' }}>₹{v.a.toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></div>
            </div>
          ))}
          <div style={{ background: 'var(--primary)', color: '#fff', border: '1px solid var(--primary)', borderRadius: 'var(--radius)', padding: '12px 16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 12, opacity: 0.9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Grand Total</div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>₹{grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
